import { TaleFyiNativeAlignment } from './testAlignStorage';

export interface TaleDomItem {
  id: string;
  text: string;
  tag?: string;
  class?: string;
  [key: string]: any;
}

export interface ModelEvaluation {
  offset: number;
  name: string;
  description: string;
  avgCharsPerSec: number;
  minCharsPerSec: number;
  maxCharsPerSec: number;
  stdDev: number;
  plausibleCount: number;
  implausibleCount: number;
  isRecommended: boolean;
  notes: string;
}

export interface ComparisonSample {
  domIndex: number;
  domId: string;
  domPreview: string;
  domLength: number;
  paraIndex?: number;
  paraId?: string;
  startTime?: number;
  duration?: number;
  phraseCount?: number;
  charsPerSec?: number;
  status: 'narrativo' | 'metadados' | 'indice' | 'sem_audio' | 'discrepante';
  statusLabel: string;
}

export interface TaleDiagnosticReport {
  alignmentStats: {
    parasCount: number;
    phraseBeginsCount: number;
    phraseIndexesCount: number;
    zeroCount: number;
    zeroMatchesParas: boolean;
    totalDuration: number;
    avgPhrasesPerPara: number;
    minPhrasesPerPara: number;
    maxPhrasesPerPara: number;
  };
  domStats: {
    totalItems: number;
    totalCharacters: number;
    totalWords: number;
    avgCharsPerItem: number;
    firstItemsLengths: number[];
  };
  idAnalysis: {
    matchingIdsCount: number;
    matchingIds: string[];
    hasDirectIdMatch: boolean;
    parasIdSample: string[];
    domIdSample: string[];
    idFormatObservation: string;
  };
  segmentationAnalysis: {
    firstParasDurations: number[];
    firstDomLengths: number[];
    directRatioObservation: string;
    models: ModelEvaluation[];
    recommendedOffset: number;
  };
  samples: {
    beginning: ComparisonSample[];
    middle: ComparisonSample[];
    end: ComparisonSample[];
  };
  nonNarrativeBlocks: {
    initial: Array<{ domIndex: number; id: string; preview: string; length: number }>;
    trailing: Array<{ domIndex: number; id: string; preview: string; length: number }>;
  };
  summarySummary: string;
}

export class TaleAlignDiagnosticService {
  /**
   * Extrai e valida a estrutura de alinhamento original Tale.fyi
   */
  static validateAlignment(parsed: any): { isValid: boolean; error?: string; alignment?: TaleFyiNativeAlignment } {
    if (!parsed || typeof parsed !== 'object') {
      return { isValid: false, error: 'O arquivo não é um objeto JSON válido.' };
    }
    if (!parsed.paras || !Array.isArray(parsed.paras)) {
      return { isValid: false, error: 'Campo obrigatório "paras" não encontrado ou não é um array.' };
    }
    if (!parsed.phraseBegins || !Array.isArray(parsed.phraseBegins)) {
      return { isValid: false, error: 'Campo obrigatório "phraseBegins" não encontrado ou não é um array.' };
    }
    if (!parsed.phraseIndexes || !Array.isArray(parsed.phraseIndexes)) {
      return { isValid: false, error: 'Campo obrigatório "phraseIndexes" não encontrado ou não é um array.' };
    }

    const alignment: TaleFyiNativeAlignment = {
      paras: parsed.paras,
      phraseBegins: parsed.phraseBegins.map((n: any) => parseFloat(n) || 0),
      phraseIndexes: parsed.phraseIndexes.map((n: any) => parseInt(n, 10) || 0),
      title: parsed.title || parsed.book_title || '',
      author: parsed.author || '',
      language: parsed.language || parsed.lang || 'pt',
      total_duration_seconds: parsed.total_duration_seconds || parsed.duration || parsed.phraseBegins[parsed.phraseBegins.length - 1] || 0
    };

    return { isValid: true, alignment };
  }

  /**
   * Extrai e valida a estrutura do arquivo de texto (tale_dom.json ou HTML)
   */
  static parseDomContent(content: string): { isValid: boolean; error?: string; items?: TaleDomItem[]; isJson: boolean } {
    const trimmed = content.trim();
    if (!trimmed) {
      return { isValid: false, error: 'O conteúdo do arquivo de texto está vazio.', isJson: false };
    }

    // Tentativa 1: JSON Array direto de objetos { id, text }
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        const rawArray: any[] = Array.isArray(parsed) ? parsed : (parsed.items || parsed.elements || parsed.paragraphs || parsed.data || []);
        if (Array.isArray(rawArray) && rawArray.length > 0) {
          const items: TaleDomItem[] = rawArray.map((item, idx) => ({
            id: String(item.id || item.dom_id || `dom_${idx}`),
            text: String(item.text || item.content || item.sentence || '').trim(),
            tag: item.tag || 'p',
            class: item.class || ''
          }));
          return { isValid: true, items, isJson: true };
        }
      } catch {
        // Se falhar o parse de JSON, continua para parse HTML abaixo
      }
    }

    // Tentativa 2: Parse de HTML / DOM
    try {
      if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        const elements = doc.querySelectorAll('p, div.paragraph, div.para, .audio-para-item, h1, h2, h3, header');
        if (elements.length > 0) {
          const items: TaleDomItem[] = Array.from(elements).map((el, idx) => ({
            id: el.id || `html_node_${idx}`,
            text: (el.textContent || '').trim(),
            tag: el.tagName.toLowerCase(),
            class: el.className
          })).filter(i => i.text.length > 0);

          return { isValid: true, items, isJson: false };
        }
      }
    } catch {}

    // Tentativa 3: Texto plano linha por linha
    const lines = trimmed.split(/\r?\n\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length > 0) {
      const items: TaleDomItem[] = lines.map((text, idx) => ({
        id: `line_${idx}`,
        text,
        tag: 'p'
      }));
      return { isValid: true, items, isJson: false };
    }

    return { isValid: false, error: 'Não foi possível extrair elementos de texto válidos do arquivo.', isJson: false };
  }

  /**
   * Executa a auditoria e diagnóstico aprofundado dos dois conjuntos de dados
   */
  static runDiagnosis(alignment: TaleFyiNativeAlignment, domItems: TaleDomItem[]): TaleDiagnosticReport {
    const paras = alignment.paras || [];
    const phraseBegins = alignment.phraseBegins || [];
    const phraseIndexes = alignment.phraseIndexes || [];

    // 1. Estatísticas de alinhamento
    let zeroCount = 0;
    const phrasesPerPara: number[] = [];
    let currentCount = 0;

    for (let i = 0; i < phraseIndexes.length; i++) {
      if (phraseIndexes[i] === 0) {
        zeroCount++;
        if (zeroCount > 1) {
          phrasesPerPara.push(currentCount);
        }
        currentCount = 1;
      } else {
        currentCount++;
      }
    }
    if (currentCount > 0) phrasesPerPara.push(currentCount);

    const totalDuration = alignment.total_duration_seconds || (phraseBegins.length > 0 ? phraseBegins[phraseBegins.length - 1] : 0);
    const avgPhrases = phrasesPerPara.length > 0 ? phrasesPerPara.reduce((a, b) => a + b, 0) / phrasesPerPara.length : 0;
    const minPhrases = phrasesPerPara.length > 0 ? Math.min(...phrasesPerPara) : 0;
    const maxPhrases = phrasesPerPara.length > 0 ? Math.max(...phrasesPerPara) : 0;

    // 2. Estatísticas do DOM / Texto
    const totalChars = domItems.reduce((acc, item) => acc + item.text.length, 0);
    const totalWords = domItems.reduce((acc, item) => acc + (item.text.match(/\S+/g)?.length || 0), 0);
    const avgCharsPerItem = domItems.length > 0 ? totalChars / domItems.length : 0;
    const firstItemsLengths = domItems.slice(0, 5).map(i => i.text.length);

    // 3. Análise de IDs
    const parasIds = new Set(paras.map(p => String(p[0])));
    const domIds = domItems.map(d => d.id);
    const matchingIds = domIds.filter(id => parasIds.has(id));

    const parasIdSample = paras.slice(0, 3).map(p => String(p[0]));
    const domIdSample = domItems.slice(0, 3).map(d => d.id);

    const idFormatObservation = matchingIds.length > 0
      ? `Encontrados ${matchingIds.length} IDs em comum entre os dois arquivos.`
      : `IDs completamente distintos: 'paras' usa formato estilo '${parasIdSample[0] || 'id00004'}' (manifesto/EPUB) enquanto 'tale_dom' usa formato estilo '${domIdSample[0] || '0px--lj6192'}' (hash de exportação do leitor Tale.fyi). A correspondência precisa ser estrutural/narrativa.`;

    // 4. Duração de cada parágrafo no alinhamento
    const paraDurations: number[] = [];
    for (let i = 0; i < paras.length; i++) {
      const start = typeof paras[i][1] === 'number' ? paras[i][1] : parseFloat(String(paras[i][1])) || 0;
      const nextStart = i + 1 < paras.length 
        ? (typeof paras[i + 1][1] === 'number' ? paras[i + 1][1] : parseFloat(String(paras[i + 1][1])) || totalDuration)
        : totalDuration;
      paraDurations.push(Math.max(0.5, nextStart - start));
    }

    // 5. Avaliação dos Modelos de Mapeamento (Offsets: 0, +2, -2)
    const testOffsets = [
      {
        offset: 2,
        name: 'Modelo Narrativo (Offset +2) — Recomendado',
        description: 'Considera que os 2 primeiros elementos de tale_dom são apresentação/metadados e mapeia os 272 itens narrativos (dom[2..273]) em paras[0..271], e os 2 finais em paras[272..273].'
      },
      {
        offset: 0,
        name: 'Modelo Sequencial Direto (Offset 0)',
        description: 'Mapeia estritamente pela ordem ordinal: dom[0] com paras[0], dom[1] com paras[1], etc.'
      },
      {
        offset: -2,
        name: 'Modelo Inverso (Offset -2)',
        description: 'Considera que paras[0] e paras[1] são áudios de introdução e mapeia dom[0] com paras[2].'
      }
    ];

    const modelEvaluations: ModelEvaluation[] = testOffsets.map(({ offset, name, description }) => {
      const speeds: number[] = [];
      let plausible = 0;
      let implausible = 0;

      // Amostrar nos 272 itens narrativos
      const checkCount = Math.min(272, paras.length);
      for (let pIdx = 0; pIdx < checkCount; pIdx++) {
        const dIdx = pIdx + offset;
        if (dIdx >= 0 && dIdx < domItems.length) {
          const charLen = domItems[dIdx].text.length;
          const duration = paraDurations[pIdx] || 1;
          const speed = charLen / duration;
          speeds.push(speed);

          // Velocidade humana plausível de leitura em voz alta: entre 9 e 24 caracteres por segundo
          if (speed >= 9 && speed <= 25) {
            plausible++;
          } else {
            implausible++;
          }
        }
      }

      const avgSpeed = speeds.length > 0 ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0;
      const minSpeed = speeds.length > 0 ? Math.min(...speeds) : 0;
      const maxSpeed = speeds.length > 0 ? Math.max(...speeds) : 0;

      // Desvio padrão da velocidade
      const variance = speeds.length > 0 ? speeds.reduce((acc, s) => acc + Math.pow(s - avgSpeed, 2), 0) / speeds.length : 0;
      const stdDev = Math.sqrt(variance);

      return {
        offset,
        name,
        description,
        avgCharsPerSec: avgSpeed,
        minCharsPerSec: minSpeed,
        maxCharsPerSec: maxSpeed,
        stdDev,
        plausibleCount: plausible,
        implausibleCount: implausible,
        isRecommended: false,
        notes: ''
      };
    });

    // Determinar o modelo mais plausível (maior contagem de plausíveis e taxa de fala mais próxima de 14 ch/s)
    let bestModelIdx = 0;
    let highestScore = -Infinity;

    modelEvaluations.forEach((m, idx) => {
      // Score privilegia velocidade média próxima de 14.5 ch/s e maior número de blocos plausíveis
      const speedDistance = Math.abs(m.avgCharsPerSec - 14.5);
      const score = (m.plausibleCount * 2) - (m.implausibleCount * 1.5) - (speedDistance * 3);
      if (score > highestScore) {
        highestScore = score;
        bestModelIdx = idx;
      }
    });

    modelEvaluations[bestModelIdx].isRecommended = true;
    const recommendedOffset = modelEvaluations[bestModelIdx].offset;

    // 6. Construir Amostras de Comparação Lado a Lado (Início, Meio e Fim)
    const buildSample = (dIdx: number, pIdx?: number): ComparisonSample => {
      const dItem = domItems[dIdx];
      const hasPara = pIdx !== undefined && pIdx >= 0 && pIdx < paras.length;
      const pRecord = hasPara ? paras[pIdx] : undefined;
      const pId = pRecord ? String(pRecord[0]) : undefined;
      const start = pRecord ? (typeof pRecord[1] === 'number' ? pRecord[1] : parseFloat(String(pRecord[1])) || 0) : undefined;
      const dur = hasPara ? paraDurations[pIdx] : undefined;
      const phrases = hasPara ? (phrasesPerPara[pIdx] || 1) : undefined;
      const chLen = dItem ? dItem.text.length : 0;
      const cps = dur && dur > 0 ? chLen / dur : undefined;

      let status: ComparisonSample['status'] = 'narrativo';
      let statusLabel = 'Narrativo Sincronizado';

      if (dIdx < 2 && recommendedOffset === 2) {
        status = 'metadados';
        statusLabel = 'Apresentação / Metadados';
      } else if (dIdx >= 274 && domItems.length >= 276) {
        status = 'indice';
        statusLabel = 'Capítulos / Índice';
      } else if (!hasPara) {
        status = 'sem_audio';
        statusLabel = 'Sem Registro de Áudio';
      } else if (cps && (cps < 6 || cps > 35)) {
        status = 'discrepante';
        statusLabel = `Taxa atípica (${cps.toFixed(1)} ch/s)`;
      }

      return {
        domIndex: dIdx,
        domId: dItem ? dItem.id : `dom_${dIdx}`,
        domPreview: dItem ? (dItem.text.slice(0, 65) + (dItem.text.length > 65 ? '...' : '')) : '(vazio)',
        domLength: chLen,
        paraIndex: pIdx,
        paraId: pId,
        startTime: start,
        duration: dur,
        phraseCount: phrases,
        charsPerSec: cps,
        status,
        statusLabel
      };
    };

    // Amostras Início (0 a 4)
    const beginningSamples: ComparisonSample[] = [];
    for (let d = 0; d < Math.min(5, domItems.length); d++) {
      const p = d - recommendedOffset;
      beginningSamples.push(buildSample(d, p >= 0 && p < paras.length ? p : undefined));
    }

    // Amostras Meio (em torno de 136)
    const middleSamples: ComparisonSample[] = [];
    const midDom = Math.floor(domItems.length / 2);
    for (let d = Math.max(0, midDom - 2); d <= Math.min(domItems.length - 1, midDom + 2); d++) {
      const p = d - recommendedOffset;
      middleSamples.push(buildSample(d, p >= 0 && p < paras.length ? p : undefined));
    }

    // Amostras Fim (últimos 5)
    const endSamples: ComparisonSample[] = [];
    for (let d = Math.max(0, domItems.length - 5); d < domItems.length; d++) {
      let p: number | undefined = d - recommendedOffset;
      // Para os dois elementos finais quando offset=2: dom[274] -> paras[272] e dom[275] -> paras[273]
      if (d === domItems.length - 2 && paras.length >= 273) p = paras.length - 2;
      if (d === domItems.length - 1 && paras.length >= 274) p = paras.length - 1;
      endSamples.push(buildSample(d, p !== undefined && p >= 0 && p < paras.length ? p : undefined));
    }

    // Identificação de Blocos Não-Narrativos
    const initialNonNarrative = domItems.slice(0, recommendedOffset).map((item, idx) => ({
      domIndex: idx,
      id: item.id,
      preview: item.text.slice(0, 80),
      length: item.text.length
    }));

    const trailingNonNarrative = domItems.slice(Math.max(0, domItems.length - 2)).map((item, idx) => ({
      domIndex: domItems.length - 2 + idx,
      id: item.id,
      preview: item.text.slice(0, 80),
      length: item.text.length
    }));

    const directRatioObs = `No início, o intervalo paras[0]->paras[1] tem ${(paraDurations[0] || 0).toFixed(1)}s e paras[1]->paras[2] tem ${(paraDurations[1] || 0).toFixed(1)}s. O bloco inicial de texto possui ${firstItemsLengths[0] || 0} caracteres.`;

    const summary = `Validação Concluída: alinhamento possui ${paras.length} paras e ${phraseBegins.length} frases; texto possui ${domItems.length} blocos. Modelo recomendado: ${modelEvaluations[bestModelIdx].name} com taxa média de fala de ${modelEvaluations[bestModelIdx].avgCharsPerSec.toFixed(1)} chars/segundo.`;

    return {
      alignmentStats: {
        parasCount: paras.length,
        phraseBeginsCount: phraseBegins.length,
        phraseIndexesCount: phraseIndexes.length,
        zeroCount,
        zeroMatchesParas: zeroCount === paras.length,
        totalDuration,
        avgPhrasesPerPara: avgPhrases,
        minPhrasesPerPara: minPhrases,
        maxPhrasesPerPara: maxPhrases
      },
      domStats: {
        totalItems: domItems.length,
        totalCharacters: totalChars,
        totalWords,
        avgCharsPerItem,
        firstItemsLengths
      },
      idAnalysis: {
        matchingIdsCount: matchingIds.length,
        matchingIds,
        hasDirectIdMatch: matchingIds.length > 0,
        parasIdSample,
        domIdSample,
        idFormatObservation
      },
      segmentationAnalysis: {
        firstParasDurations: paraDurations.slice(0, 3),
        firstDomLengths: firstItemsLengths.slice(0, 3),
        directRatioObservation: directRatioObs,
        models: modelEvaluations,
        recommendedOffset
      },
      samples: {
        beginning: beginningSamples,
        middle: middleSamples,
        end: endSamples
      },
      nonNarrativeBlocks: {
        initial: initialNonNarrative,
        trailing: trailingNonNarrative
      },
      summarySummary: summary
    };
  }

  /**
   * Constrói o HTML processado a partir dos domItems garantindo:
   * 1. Preservação integral dos textos originais.
   * 2. Marcação precisa de data-para-idx e data-para-start nos elementos narrativos.
   * 3. Isolamento dos elementos não-narrativos (metadados/apresentação inicial e índices finais)
   *    para que não recebam classe de sincronia indevida nem causem salto no clique.
   */
  static generatePreparedHtml(
    alignment: TaleFyiNativeAlignment,
    domItems: TaleDomItem[],
    offset: number = 2
  ): string {
    const paras = alignment.paras || [];
    const htmlParts: string[] = [];

    domItems.forEach((item, dIdx) => {
      const text = item.text || '';
      const safeId = item.id ? item.id.replace(/"/g, '&quot;') : `dom_${dIdx}`;

      // Caso A: Elementos Iniciais de Metadados (Apresentação antes do início narrativo)
      if (dIdx < offset) {
        htmlParts.push(`
          <div 
            id="${safeId}" 
            data-dom-idx="${dIdx}"
            class="tale-meta-intro-block my-4 p-4 rounded-2xl bg-slate-900/40 border border-slate-800 text-slate-400 text-sm select-text transition-colors"
          >
            <div className="flex items-center gap-2 mb-1.5 text-[10px] font-mono font-bold uppercase tracking-wider text-amber-500/70">
              <span>📄 Apresentação / Metadados (Não-Narrativo)</span>
            </div>
            <p className="leading-relaxed">${escapeHtml(text)}</p>
          </div>
        `.trim());
        return;
      }

      // Caso B: Elementos Finais (Capítulos e Índice - ex: itens 274 e 275 em lista de 276)
      if (dIdx >= domItems.length - 2 && domItems.length >= 276) {
        // Se houver registros específicos em paras para o final (ex: paras[272] e paras[273])
        const finalPIdx = paras.length - (domItems.length - dIdx);
        const hasFinalAudio = finalPIdx >= 0 && finalPIdx < paras.length;

        if (hasFinalAudio) {
          const startVal = typeof paras[finalPIdx][1] === 'number' ? paras[finalPIdx][1] : parseFloat(String(paras[finalPIdx][1])) || 0;
          htmlParts.push(`
            <div 
              id="${safeId}" 
              data-dom-idx="${dIdx}"
              data-para-idx="${finalPIdx}"
              data-para-start="${startVal}"
              class="audio-para-item tale-index-block my-4 p-4 rounded-2xl bg-slate-900/40 border border-amber-500/20 text-slate-300 text-sm select-text cursor-pointer transition-colors"
            >
              <div class="flex items-center gap-2 mb-1.5 text-[10px] font-mono font-bold uppercase tracking-wider text-amber-400">
                <span>📑 Índice / Fim da Obra (Áudio: ${startVal.toFixed(1)}s)</span>
              </div>
              <p class="leading-relaxed font-semibold">${escapeHtml(text)}</p>
            </div>
          `.trim());
        } else {
          htmlParts.push(`
            <div 
              id="${safeId}" 
              data-dom-idx="${dIdx}"
              class="tale-index-block my-4 p-4 rounded-2xl bg-slate-900/40 border border-slate-800 text-slate-400 text-sm select-text"
            >
              <div class="flex items-center gap-2 mb-1.5 text-[10px] font-mono font-bold uppercase tracking-wider text-slate-500">
                <span>📑 Índice / Não-Sincronizado</span>
              </div>
              <p class="leading-relaxed">${escapeHtml(text)}</p>
            </div>
          `.trim());
        }
        return;
      }

      // Caso C: Parágrafo Narrativo Normal da História
      const pIdx = dIdx - offset;
      if (pIdx >= 0 && pIdx < paras.length) {
        const startVal = typeof paras[pIdx][1] === 'number' ? paras[pIdx][1] : parseFloat(String(paras[pIdx][1])) || 0;
        htmlParts.push(`
          <p 
            id="${safeId}" 
            data-dom-idx="${dIdx}"
            data-para-idx="${pIdx}"
            data-para-start="${startVal}"
            class="audio-para-item tale-narrative-para"
          >
            ${escapeHtml(text)}
          </p>
        `.trim());
      } else {
        // Fallback caso exceda paras sem áudio
        htmlParts.push(`
          <p 
            id="${safeId}" 
            data-dom-idx="${dIdx}"
            class="tale-narrative-para text-slate-400"
          >
            ${escapeHtml(text)}
          </p>
        `.trim());
      }
    });

    return htmlParts.join('\n\n');
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
